/* oxlint-disable max-lines-per-function */
import { type FC } from 'react';
import { createRoot } from 'react-dom/client';
import { type auto, type IAugmentedJQuery, type IChangesObject, type IComponentOptions } from 'angular';
import { type AnyObject } from '@Types';
import { storeProvider as _storeProvider, storeProvider } from 'ReduxHelpers';
import { ReactWrapper } from 'FrontRoyalReact/ReactWrapper';

type OnChanges<T> = {
    [K in keyof T]: IChangesObject<T[K]>;
};
type EmptyObject = AnyObject<never>;
type Bindings<T> = {
    [key in keyof T]: '<';
};

const didPropsChange = <T extends AnyObject>(newProps: Partial<T>, oldProps: Partial<T>) =>
    Object.entries(newProps).some(([key, value]) => oldProps[key] !== value);

const LEAVE_ANIMATION_FALLBACK_MS = 1000;
const LEAVE_ELEMENT_LOOKUP_ATTEMPTS = 5;

export const react2Angular = <Props extends AnyObject = EmptyObject>(
    Component: FC<Props>,
    bindingNames: (keyof Props)[] = [],
    classNames = '',
): IComponentOptions => {
    const bindings = bindingNames.reduce((names, next) => ({ ...names, ...{ [next]: '<' } }), {} as Bindings<Props>);

    const controller = [
        '$element',
        '$injector',
        class {
            isInitialRender = true;
            props = {} as Props;
            isDestroyed = false;
            isUnmountScheduled = false;
            root: ReturnType<typeof createRoot>;
            injector: auto.IInjectorService;

            constructor(
                private $element: IAugmentedJQuery,
                $injector: auto.IInjectorService,
            ) {
                this.root = createRoot(this.$element[0]);
                this.injector = $injector;
            }

            static get $$ngIsClass() {
                return true;
            }

            $onChanges(changes: OnChanges<Partial<Props>>) {
                const prevProps = this.props;

                const updatedProps = Object.entries(changes).reduce<Partial<Props>>(
                    (props, [key, { currentValue }]) => {
                        props[key as keyof Partial<Props>] = currentValue;
                        return props;
                    },
                    {},
                );

                const nextProps = { ...this.props, ...updatedProps };

                if (this.isInitialRender) {
                    this.props = nextProps;
                    this.renderReactComponent();
                    this.isInitialRender = false;
                } else if (didPropsChange(updatedProps, prevProps)) {
                    this.props = nextProps;
                    this.renderReactComponent();
                }
            }

            $onDestroy() {
                this.scheduleUnmountWithAnimation(0);
            }

            unmountReactComponent() {
                this.isDestroyed = true;
                this.root.unmount();
            }

            findLeavingElement(): Element | null {
                const node = this.$element[0] as Element | undefined;
                if (!node) {
                    return null;
                }

                let current: Element | null = node;
                while (current) {
                    if (current.classList?.contains('ng-leave')) {
                        return current;
                    }
                    current = current.parentElement;
                }

                return null;
            }

            scheduleUnmountWithAnimation(attempt: number) {
                if (this.isDestroyed || this.isUnmountScheduled) return;

                const leavingElement = this.findLeavingElement();

                if (leavingElement) {
                    this.isUnmountScheduled = true;
                    this.unmountAfterLeaveAnimation(leavingElement);
                    return;
                }

                if (attempt >= LEAVE_ELEMENT_LOOKUP_ATTEMPTS) {
                    this.unmountReactComponent();
                    return;
                }

                const $window = this.injector.get<Window>('$window');
                const queueNextAttempt =
                    $window.requestAnimationFrame?.bind($window) ??
                    ((cb: FrameRequestCallback) => $window.setTimeout(() => cb($window.performance.now()), 16));

                queueNextAttempt(() => this.scheduleUnmountWithAnimation(attempt + 1));
            }

            unmountAfterLeaveAnimation(leavingElement: Element) {
                const $window = this.injector.get<Window>('$window');
                let finished = false;
                let fallbackTimeoutId: number;

                const finalize = () => {
                    if (finished) {
                        return;
                    }
                    finished = true;
                    leavingElement.removeEventListener('transitionend', finalize);
                    leavingElement.removeEventListener('animationend', finalize);
                    $window.clearTimeout(fallbackTimeoutId);
                    this.unmountReactComponent();
                };

                fallbackTimeoutId = $window.setTimeout(finalize, LEAVE_ANIMATION_FALLBACK_MS);

                leavingElement.addEventListener('transitionend', finalize);
                leavingElement.addEventListener('animationend', finalize);
            }

            renderReactComponent() {
                const $window = this.injector.get<Window>('$window');

                // In lesson preview mode, the storeProvider is attached to the iframe.contentWindow
                // See edit_lesson_preview_mode_dir.js
                const currentStoreProvider = ($window.storeProvider || storeProvider) as typeof storeProvider;

                if (!this.isDestroyed) {
                    this.root.render(
                        <ReactWrapper
                            angularInjector={this.injector}
                            classNames={classNames}
                            store={currentStoreProvider.store!}
                        >
                            <Component {...this.props} />
                        </ReactWrapper>,
                    );
                }
            }
        },
    ] as IComponentOptions['controller'];

    return { bindings, controller };
};

export default react2Angular;
